import React from "react";

interface Props {
  equipe?: string | number | null;
  chefEquipe?: string | number | null;
  chauffeur?: string | number | null;
  vehicule?: string | number | null;

  // 🛰 Backend checks
  vehiculeDisponible?: boolean;
  chauffeurDisponible?: boolean;
  capaciteEquipe?: number;
  missionsActivesEquipe?: number;
  conflitPlanning?: boolean;

  onReaffecter?: () => void;
}

/**
 * ✅ Affichage sécurisé — évite les crash trim()
 */
const display = (value?: unknown) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed !== "" ? trimmed : "—";
  }

  if (value === null || value === undefined) {
    return "—";
  }

  return String(value);
};

const MissionAffectationCard: React.FC<Props> = ({
  equipe,
  chefEquipe,
  chauffeur,
  vehicule,

  vehiculeDisponible = true,
  chauffeurDisponible = true,
  capaciteEquipe = 0,
  missionsActivesEquipe = 0,
  conflitPlanning = false,

  onReaffecter,
}) => {
  const isComplete = Boolean(
    equipe && chefEquipe && chauffeur && vehicule
  );

  const safeCapacite = Math.max(capaciteEquipe ?? 0, 0);
  const safeMissions = Math.max(missionsActivesEquipe ?? 0, 0);

  const surcharge =
    safeCapacite > 0 && safeMissions >= safeCapacite;

  const hasCriticalIssue =
    !vehiculeDisponible || !chauffeurDisponible;

  const affectationValide =
    isComplete &&
    vehiculeDisponible &&
    chauffeurDisponible &&
    !conflitPlanning &&
    !surcharge;

  return (
    <div className="bg-slate-900/40 p-5 rounded-xl border border-slate-700 shadow-md space-y-5">
      <h2 className="font-semibold text-base text-gray-200">
        🚛 Affectation
      </h2>

      {/* GRID PRINCIPAL */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-300">
        <div>
          <span className="font-medium text-gray-400">
            👥 Équipe :
          </span>{" "}
          {display(equipe)}
        </div>

        <div>
          <span className="font-medium text-gray-400">
            👷 Chef :
          </span>{" "}
          {display(chefEquipe)}
        </div>

        <div>
          <span className="font-medium text-gray-400">
            👨‍✈️ Chauffeur :
          </span>{" "}
          {display(chauffeur)}
        </div>

        <div>
          <span className="font-medium text-gray-400">
            🚛 Véhicule :
          </span>{" "}
          {display(vehicule)}
        </div>
      </div>

      {/* ALERTES BACKEND */}
      {!vehiculeDisponible && (
        <div className="bg-red-600/20 text-red-400 p-3 rounded text-xs border border-red-600/40">
          🚨 Véhicule déjà affecté à une autre mission.
        </div>
      )}

      {!chauffeurDisponible && (
        <div className="bg-yellow-600/20 text-yellow-400 p-3 rounded text-xs border border-yellow-600/40">
          ⚠️ Chauffeur déjà engagé sur une autre mission.
        </div>
      )}

      {conflitPlanning && (
        <div className="bg-orange-600/20 text-orange-400 p-3 rounded text-xs border border-orange-600/40">
          🧠 Conflit détecté dans le planning.
        </div>
      )}

      {surcharge && (
        <div className="bg-purple-600/20 text-purple-400 p-3 rounded text-xs border border-purple-600/40">
          📊 Équipe en surcharge ({safeMissions}/{safeCapacite} missions).
        </div>
      )}

      {/* STATUT GLOBAL */}
      <div
        className={`text-xs p-3 rounded border ${
          affectationValide
            ? "bg-green-600/20 text-green-400 border-green-600/40"
            : "bg-slate-700/30 text-gray-400 border-slate-600"
        }`}
      >
        {affectationValide
          ? "✅ Affectation valide — Mission prête"
          : hasCriticalIssue
          ? "❌ Affectation invalide — Conflit critique"
          : "⏳ Affectation nécessite vérification"}
      </div>

      {/* RÉAFFECTATION */}
      {onReaffecter && (
        <div className="pt-3 border-t border-slate-700">
          <button
            onClick={onReaffecter}
            disabled={hasCriticalIssue}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              hasCriticalIssue
                ? "bg-slate-600 text-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            🔄 Réaffecter mission
          </button>
        </div>
      )}
    </div>
  );
};

export default MissionAffectationCard;